<script lang="ts">
    import type {TabButton, TabId, TabPageDefinitionsMap} from 'shared/ui-widgets/tabset/types';
    import type {RelatedRecordsTable} from '../../../record/kp-related-records-table/types';
    import type {User} from 'typings/portaro.be.types';
    import type {UserDetailView} from 'src/features/user/types';
    import type {SvelteComponentConstructor} from 'core/types';
    import UserDataService from 'src/features/user/user.data-service';
    import {getPageContext} from 'shared/layouts/page-context';
    import {getInjector} from 'core/svelte-context/context';
    import {onMount} from 'svelte';
    import {ErpFondPermissionsContextService} from 'src/features/erp';
    import {FOND_PERSON, FOND_PROJECT} from 'src/features/erp-sutor/sutor-fonds';
    import {EmployeeRecordDetail} from 'src/features/erp-sutor/features/employee-record-detail';
    import {ProjectRecordDetail, ProjectTopbar} from 'src/features/erp-sutor/features/project-record-detail';
    import {ErpPageLayout, ErpTabbedSubpagesContainer, TabbedSubpagesRouter} from 'src/features/erp/components';
    import {ProjectLogbookPairing} from 'src/features/erp-sutor/features/project-logbook-pairing';
    import {ProjectReportOptimisation} from 'src/features/erp-sutor/features/project-report-optimisation';
    import {MediaViewerInline} from 'src/features/media-viewer';
    import KpRelatedRecordsTable from '../../../record/kp-related-records-table/KpRelatedRecordsTable.svelte';
    import GenericRecordDetailDetailTab from './tab-pages/GenericRecordDetailDetailTab.svelte';
    import GenericRecordDetailExportTab from './tab-pages/export/GenericRecordDetailExportTab.svelte';
    import GenericRecordDetailThreadTab from './tab-pages/GenericRecordDetailThreadTab.svelte';
    import SutorUserAccountAttendanceTab
        from 'src/features/user/kp-user-account-page/sutor-parts/tab-pages/SutorUserAccountAttendanceTab.svelte';
    import GenericRecordDetailTopbar from './components/GenericRecordDetailTopbar.svelte';
    import type {
        DocumentDetailReactivePageData,
        DocumentDetailStaticPageData
    } from '../../../record/kp-document-detail-page/types';

    export let relatedTables: RelatedRecordsTable[] = [];

    const userDataService = getInjector().getByClass(UserDataService);
    const fondPermissionsService = getInjector().getByClass(ErpFondPermissionsContextService);

    const pageContext = getPageContext<DocumentDetailStaticPageData, DocumentDetailReactivePageData>();
    const fondPermissions = fondPermissionsService.getFondPermissionsValue();
    const model = pageContext.staticData.model;
    const record = model.record;
    const isRecordProject = FOND_PROJECT.subfonds.includes(record.fond.id);
    const isProjectReported = isRecordProject && record.fond.id === FOND_PROJECT.REPORTED.fond;

    let loading = true;
    let loadedUser: User | null = null;
    let loadedUserDetail: UserDetailView | null = null;

    const isUserRecord = FOND_PERSON.fond === record.fond.id;

    const tabButtons: TabButton[] = [
        {
            id: 'tab-detail',
            label: isRecordProject ? 'Přehledy' : 'Přehled',
            icon: 'info',
            tabPageWithoutPadding: isRecordProject
        }
    ];

    let tabs = createTabs(null, null);

    // Add related tables tabs
    relatedTables.forEach((relatedTable) => {
        tabButtons.push({
            id: `tab-relatedtable-${relatedTable.tableId}`,
            label: relatedTable.title,
            icon: 'grid-alt',
            editPermissionFond: relatedTable.fondId,
            tabPageWithoutPadding: true,
            tabPageContainerClass: 'record-grid-tab-page'
        });
    });

    // Add an optimisation tab for project fonds
    if (isRecordProject) {
        tabButtons.push({
            id: 'tab-optimisation',
            label: 'Optimalizace nákladů',
            icon: 'money-check-edit',
            tabPageWithoutPadding: true
        });

        if (isProjectReported) {
            tabButtons.push({
                id: 'tab-logbook-pairing',
                label: 'Párování montážních deníků',
                icon: 'arrows-cross',
                tabPageWithoutPadding: true
            });
        }
    }

    // Add a user attendance tab for user records
    if (isUserRecord) {
        tabButtons.push({
            id: 'tab-user-attendance',
            label: 'Docházka',
            icon: 'calendar'
        });
    }

    // Only add Konverzace, Soubory and Tisk tabs if we have edit permissions
    if (fondPermissionsService.hasEditFondPermissions(fondPermissions, record.fond.id)) {
        // Add a thread tab if applicable
        if (record.fond.threadable) {
            tabButtons.push({
                id: 'tab-thread',
                label: 'Konverzace',
                icon: 'messages',
                tabPageWithoutPadding: true
            });
        }

        // Add common tabs
        tabButtons.push(
            {
                id: 'tab-files',
                label: 'Soubory',
                icon: 'file',
                tabPageWithoutPadding: true
            },
            {
                id: 'tab-export',
                label: 'Exporty',
                icon: 'file-export',
                tabPageWithoutPadding: true
            }
        );
    }

    onMount(async () => {
        if (!isUserRecord) {
            loading = false;
            return;
        }

        try {
            loadedUser = await userDataService.getUser(record.id);
            loadedUserDetail = await userDataService.getUserDetailView(record.id);
        } catch {
            loadedUser = null;
            loadedUserDetail = null;
        }

        tabs = createTabs(loadedUser, loadedUserDetail);

        loading = false;
    });

    function isTableTab(tabId: TabId): boolean {
        return tabId.includes('tab-relatedtable-');
    }

    function createTabs(user: User | null, userDetailView: UserDetailView | null): TabPageDefinitionsMap {
        return {
            'tab-detail': {
                component: resolveDetailComponent(),
                props: isUserRecord ? {
                    user,
                    userDetailView
                } : undefined
            },
            'tab-export': {component: GenericRecordDetailExportTab},
            'tab-thread': {component: GenericRecordDetailThreadTab},
            'tab-optimisation': {
                component: ProjectReportOptimisation,
                props: {
                    record
                }
            },
            'tab-logbook-pairing': {
                component: ProjectLogbookPairing,
                props: {
                    record
                }
            },
            'tab-files': {
                component: MediaViewerInline,
                props: {record}
            },
            'tab-user-attendance': {
                component: SutorUserAccountAttendanceTab,
                props: {injectedUserRecord: record}
            }
        };
    }

    function resolveDetailComponent(): SvelteComponentConstructor {
        if (isRecordProject) {
            return ProjectRecordDetail;
        }

        if (isUserRecord) {
            return EmployeeRecordDetail;
        }

        return GenericRecordDetailDetailTab;
    }

    function resolveTopbarComponent(): SvelteComponentConstructor {
        if (isRecordProject) {
            return ProjectTopbar;
        }

        return GenericRecordDetailTopbar;
    }
</script>

<ErpPageLayout pageClass="sutor-document-detail-tabbed-page" gap="0px" withoutContentPadding {loading}>
    <svelte:component this={resolveTopbarComponent()}
                      user={loadedUser}
                      userDetailView={loadedUserDetail}/>

    <ErpTabbedSubpagesContainer {tabButtons} additionalClasses="document-detail-tabbed-subpages" let:activeTab>
        {#if isTableTab(activeTab)}
            {@const relatedTable = relatedTables.find((table) => `tab-relatedtable-${table.tableId}` === activeTab)}

            <KpRelatedRecordsTable record={model.record}
                                   fondId={relatedTable.fondId}
                                   relations={relatedTable.relations}
                                   originReferenceFieldTypeId={relatedTable.originReferenceFieldTypeId}
                                   referenceFieldTypeId={relatedTable.referenceFieldTypeId}/>
        {:else}
            <TabbedSubpagesRouter {tabs} {activeTab}/>
        {/if}
    </ErpTabbedSubpagesContainer>
</ErpPageLayout>